/**
 * 20261022 [IOS-RELAY] iOS 앱(Pentalk Browser) relay 브리지 어댑터
 * - Android web-relay(src/lib/relay/*, window.PentalkRelay)와 분리된 iOS 전용 모듈.
 * - 앱이 페이지 시작 시점에 주입하는 window.PentalkRelayIOS 를 감싼다.
 * - 객체가 없거나(구버전 앱) hello 실패/프로토콜 불일치면 기존 iOS 경로를 그대로 사용한다.
 * - 개발 버전(ConstData.IS_DEV_VALUE)에서만 사용한다.
 * - 20261104 운영 배포에서도 사용 (IOS_RELAY_RELEASE_ENABLED). 앱 브리지가 없으면(구버전 앱) 기존 경로 그대로.
 */
import ConstData from "../ConstData";

// 20261104 [IOS-RELAY] 운영(리얼) 빌드에서도 iOS 앱 relay 사용 - 카메라 전환 시 송출 유지(교사 화면 끊김 없음), 카메라 모드도 앱 마이크 사용
// false 로 바꾸고 웹만 다시 배포하면 운영은 기존 iOS 경로로 즉시 복귀 (개발 버전 IS_DEV_VALUE 는 계속 사용)
const IOS_RELAY_RELEASE_ENABLED = true;
const isIOSRelayAllowed = () => !!(ConstData.IS_DEV_VALUE || IOS_RELAY_RELEASE_ENABLED);

const LOG = "[IOS-RELAY]";
const PROTOCOL_MAJOR = 1;

let readyPromise = null;

/** 앱이 주입한 브리지 객체 (없으면 null) */
export const getIOSRelayBridge = () => {
    try {
        const r = typeof window !== "undefined" ? window.PentalkRelayIOS : null;
        if (r && typeof r.request === "function" && typeof r.onEvent === "function" && typeof r.getDocument === "function") return r;
    } catch (e) { }
    return null;
};

/** relay 사용 후보 여부 (개발 버전 또는 운영 허용 + 브리지 있음) */
export const isIOSRelayCandidate = () => !!(isIOSRelayAllowed() && getIOSRelayBridge()); // 20261104 기존 ConstData.IS_DEV_VALUE

const isValidDocument = (doc) =>
    !!(doc && doc.ok && doc.documentToken && doc.platform === "ios" && doc.protocol && doc.protocol.major === PROTOCOL_MAJOR);

/**
 * hello 결과(문서 정보)를 기다린다. 실패 시 null.
 * @param {Number} timeoutMs - 최대 대기 시간
 * @returns {Promise<Object|null>}
 */
export const ensureIOSRelayReady = (timeoutMs = 3000) => {
    if (!isIOSRelayCandidate()) return Promise.resolve(null);
    const bridge = getIOSRelayBridge();
    const now = bridge.getDocument();
    if (isValidDocument(now)) return Promise.resolve(now);
    if (readyPromise) return readyPromise;

    readyPromise = new Promise((resolve) => {
        const started = Date.now();
        const check = () => {
            const doc = bridge.getDocument();
            if (isValidDocument(doc)) {
                console.log(`${LOG} ready - documentSeq[${doc.documentSeq}], features`, doc.features);
                resolve(doc);
                return;
            }
            const state = bridge.getHelloState ? bridge.getHelloState() : "";
            if ((state && state.indexOf("rejected") === 0) || Date.now() - started > timeoutMs) {
                console.log(`${LOG} not ready - helloState[${state}], doc`, doc);
                resolve(null);
                return;
            }
            setTimeout(check, 100);
        };
        check();
    }).then((doc) => {
        readyPromise = null;
        return doc;
    });
    return readyPromise;
};

/** 현재 문서에서 해당 기능을 앱이 지원하는지 */
export const isIOSRelayFeature = (name) => {
    const bridge = getIOSRelayBridge();
    const doc = bridge ? bridge.getDocument() : null;
    return !!(isIOSRelayAllowed() && isValidDocument(doc) && doc.features && doc.features[name] === true); // 20261104 기존 ConstData.IS_DEV_VALUE
};

/** 앱으로 명령 전송 (결과 body 반환) */
export const requestIOSRelay = async (command, payload = {}, timeoutMs = 3000) => {
    const bridge = getIOSRelayBridge();
    if (!bridge) return { result: "no-bridge" };
    try {
        const body = await bridge.request(command, payload, timeoutMs);
        if (!body || body.result !== "ok") console.log(`${LOG} ${command} ->`, body);
        return body || { result: "empty" };
    } catch (e) {
        console.log(`${LOG} ${command} 오류`, e);
        return { result: "error", message: String(e) };
    }
};

/** 앱 이벤트 구독 (해제 함수 반환) */
export const onIOSRelayEvent = (fn) => {
    const bridge = getIOSRelayBridge();
    return bridge ? bridge.onEvent(fn) : () => { };
};
